import * as React from 'react';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';

import { navigate } from 'libs/router';

import Spinner from 'components/spinner/Spinner';
import Pagination from 'components/pagination/Pagination';
import PageSection from 'components/page/page-section/PageSection';
import PageHeading from 'components/page/page-heading/PageHeading';
import Input from 'components/inputs/input/Input';
import InfluencerSelector from 'components/influencer-selector/InfluencerSelector';

import ClientApprovalBucket from './ClientApprovalBucket';

import CampaignInfo from 'modules/campaigns/components/campaign-info/CampaignInfo';
import ContentApprovalSort from 'modules/campaigns/components/content-approval/ContentApprovalSort';

import {
  downloadCampaignContent,
  downloadLook,
  fetchClientApproval,
  fetchInfluencers,
  resetClientApproval,
  resetInfluencers,
  updateApprovalStatus,
  updateBrandNote
} from 'modules/shared-access/reducers/clientApproval';

import { __t, applyTranslations } from 'utils/language';
import { getLocationUrl, getLocationWithExtendedQuery } from 'utils/client';
import { buildPrefixForDownloadableCampaignContent } from 'utils/deliverables';
import { CLIENT_APPROVAL_SORT_KEYS, CLIENT_APPROVAL_SORT_OPTIONS } from 'utils/contentApproval';
import { doesMatchSearchQuery } from 'utils/influencers';

import './client-approval.scss';

import type {
  Campaign,
  ClientApprovalBucket as ClientApprovalBucketType,
  ClientApprovalCampaign,
  InfluencerProfile,
  LocationWindow,
  Pagination as PaginationType,
  SelectOption
} from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./client-approval.i18n.json'));

type UrlQueryParams = {
  creatorId?: string | number,
  page?: number,
  sort?: string
};

type StateProps = {
  location: LocationWindow,
  isLoading: boolean,
  list: Array<ClientApprovalBucketType>,
  influencers: Array<InfluencerProfile>,
  pagination: PaginationType,
  campaign: ClientApprovalCampaign | undefined | null
};

type OwnProps = {};

type DispatchProps = {
  dispatchFetchClientApproval: (influencerId: number, page: number, sortBy: string) => Promise<any>,
  dispatchFetchInfluencers: () => Promise<any>,
  dispatchDownloadCampaignContent: (filename: string) => Promise<any>,
  dispatchDownloadLook: (bucketId: number, lookName: string, campaignName: string) => Promise<any>,
  dispatchResetClientApproval: () => Promise<any>,
  dispatchResetInfluencers: () => Promise<any>,
  dispatchUpdateBrandNote: (bucketId: number, note: string) => Promise<any>,
  dispatchUpdateApprovalStatus: (bucketId: number, itemId: number, approvalStatus: string) => Promise<any>
};

type Props = StateProps & OwnProps & DispatchProps;

type State = {
  searchQuery: string,
  selectedItem: InfluencerProfile | undefined | null
};

const RESET_BUTTON_PROPS = {
  label: __t('client_approval.select_all_creators'),
  icon: 'users-connected-white-bg-custom-fg'
};

class ClientApproval extends React.PureComponent<Props, State> {
  state = {
    searchQuery: '',
    selectedItem: null
  };

  componentDidMount() {
    const { dispatchFetchInfluencers } = this.props;

    this.fetchBuckets();
    dispatchFetchInfluencers();
  }

  componentDidUpdate(prevProps: Props) {
    const { location } = this.props;
    const query = get(location, 'query', {});
    const prevPropsQuery = get(prevProps.location, 'query', {});

    if (
      query.page !== prevPropsQuery.page ||
      query.sort !== prevPropsQuery.sort ||
      query.creatorId !== prevPropsQuery.creatorId
    ) {
      this.fetchBuckets();
    }
  }

  componentWillUnmount() {
    const { dispatchResetClientApproval, dispatchResetInfluencers } = this.props;

    dispatchResetClientApproval();
    dispatchResetInfluencers();
  }

  getQueryParam(paramName: string): string {
    const { location } = this.props;
    const query = get(location, 'query', {});

    return query[paramName] || '';
  }

  getLocationUrl(params: UrlQueryParams): string {
    const { location } = this.props;

    return getLocationUrl(getLocationWithExtendedQuery(location, params));
  }

  getSortOptions(): Array<SelectOption> {
    const creatorId = this.getQueryParam('creatorId');

    return CLIENT_APPROVAL_SORT_OPTIONS.map(item => ({
      ...item,
      disabled: !isEmpty(creatorId) && item.value === 'creator.asc'
    }));
  }

  getCurrentSort(): string {
    const sort = this.getQueryParam('sort');

    return sort && CLIENT_APPROVAL_SORT_KEYS.includes(sort) ? sort : CLIENT_APPROVAL_SORT_KEYS[0];
  }

  getFilteredProfiles(): Array<InfluencerProfile> {
    const { influencers } = this.props;
    const { searchQuery } = this.state;
    let filteredProfiles = influencers;

    if (searchQuery) {
      filteredProfiles = influencers.filter(item => {
        if (!doesMatchSearchQuery(searchQuery, item)) {
          return false;
        }

        return true;
      });
    }

    return filteredProfiles;
  }

  getSelectedItem(influencerProfileId?: number | null): InfluencerProfile | undefined | null {
    const { influencers } = this.props;

    return influencers.find(profile => profile.id === influencerProfileId);
  }

  fetchBuckets(): void {
    const { dispatchFetchClientApproval } = this.props;
    const influencerId = this.getQueryParam('creatorId') || 0;
    const page = this.getQueryParam('page') || 1;

    dispatchFetchClientApproval(Number(influencerId), Number(page), this.getCurrentSort());
  }

  handleSortChanged = (currentSort: string): void => {
    const locationUrl = this.getLocationUrl({ sort: currentSort });

    navigate.to(locationUrl);
  };

  handlePageChanged = (page: number): void => {
    const locationUrl = this.getLocationUrl({ page });

    navigate.to(locationUrl);
  };

  handleApprovalStatusChanged = (bucketId: number, itemId: number, approvalStatus: string): void => {
    const { dispatchUpdateApprovalStatus } = this.props;

    dispatchUpdateApprovalStatus(bucketId, itemId, approvalStatus);
  };

  handleBrandNoteChanged = (bucketId: number, note: string): void => {
    const { dispatchUpdateBrandNote } = this.props;

    dispatchUpdateBrandNote(bucketId, note);
  };

  handleDownloadAllApproved = (): void => {
    const { campaign, dispatchDownloadCampaignContent } = this.props;
    const campaignName = (campaign || {}).name;
    const fileName = buildPrefixForDownloadableCampaignContent(null, campaignName || '');

    dispatchDownloadCampaignContent(fileName);
  };

  handleInfluencerSelected = (influencerProfileId?: number | null): void => {
    const nextSelectedItem = this.getSelectedItem(influencerProfileId);

    this.setState({
      selectedItem: nextSelectedItem
    }, () => this.handleAfterInfluencerSelected());
  };

  handleAfterInfluencerSelected(): void {
    const { selectedItem } = this.state;
    // @ts-ignore
    const selectedItemId = selectedItem ? selectedItem.id : '';
    let sort = this.getQueryParam('sort');

    if (selectedItemId) {
      sort = sort === 'creator.asc' ? 'created_at.desc' : sort;
    }

    const locationUrl = this.getLocationUrl({ page: 1, sort, creatorId: selectedItemId });

    navigate.to(locationUrl);
  }

  handleSearchChanged = (event: SyntheticEvent<HTMLInputElement>): void => {
    const target = event.currentTarget;
    const value = target.value;

    this.setState({
      searchQuery: value
    });
  };

  handleDownloadLook = (bucketId: number, lookName: string): void => {
    const { campaign, dispatchDownloadLook } = this.props;
    const campaignName = campaign ? campaign.name : __t('client_approval.campaign');

    dispatchDownloadLook(bucketId, lookName, campaignName);
  };

  renderEmpty(): React.ReactNode {
    return (
      <PageSection padding="none">
        <div className="client-approval__empty">
          {__t('client_approval.empty_message')}
        </div>
      </PageSection>
    );
  }

  renderPageHeading(): React.ReactNode {
    return (
      <PageHeading
        heading={__t('client_approval.title')}
        message={__t('client_approval.subtitle')}
        color="light"
        size="h1"
      />
    );
  }

  renderInfluencerSelector(): React.ReactNode {
    const filteredProfiles = this.getFilteredProfiles();
    const influencerId = this.getQueryParam('creatorId');

    return (
      <div className="client-approval__influencer-selector">
        <InfluencerSelector
          influencerProfiles={filteredProfiles}
          selectedInfluencerProfileId={Number(influencerId)}
          shouldSort={false}
          resetButton={RESET_BUTTON_PROPS}
          onSelect={this.handleInfluencerSelected}
        />
      </div>
    );
  }

  renderInfluencerSelectorSection(): React.ReactNode {
    const { searchQuery } = this.state;

    return (
      <div className="client-approval__influencer-selector-section">
        <div className="client-approval__influencer-selector-input">
          <Input
            name="searchQuery"
            value={searchQuery}
            width="responsive"
            placeholder={__t('client_approval.search_placeholder')}
            onChange={this.handleSearchChanged}
          />
        </div>
        {this.renderInfluencerSelector()}
      </div>
    );
  }

  renderCampaignInfo(): React.ReactNode {
    const { campaign } = this.props;

    const { hasApprovedDownloadableContents } = campaign || {};

    const contextMenuItems = [{
      name: 'download-approved',
      text: __t('client_approval.download_all_approved_content'),
      onClick: this.handleDownloadAllApproved,
      disabled: !hasApprovedDownloadableContents
    }];

    return (
      <CampaignInfo
        campaign={campaign ?
          // @ts-ignore
          ({
            ...campaign,
            mainImage: {
              url: campaign.imageUrl
            }
          } as Campaign) :
          null
        }
        pageType="client-approval"
        contextMenuItems={contextMenuItems}
      />
    );
  }

  renderApprovalBucket(): React.ReactNode {
    const { list, pagination, location } = this.props;

    return (
      <div className="client-approval__buckets">
        <div className="client-approval__params">
          <ContentApprovalSort
            sortOptions={this.getSortOptions()}
            currentSort={this.getCurrentSort()}
            onSort={this.handleSortChanged}
          />
        </div>
        {
          list.map(bucket => (
            <ClientApprovalBucket
              key={`client-approval-bucket-${bucket.id}`}
              bucket={bucket}
              onApprovalStatusChanged={this.handleApprovalStatusChanged}
              onBrandNoteChanged={this.handleBrandNoteChanged}
              onDownloadLook={this.handleDownloadLook}
            />
          ))
        }
        <Pagination
          location={location}
          meta={pagination}
          links={pagination.links}
          onClick={this.handlePageChanged}
        />
      </div>
    );
  }

  render() {
    const { isLoading, list } = this.props;

    if (isLoading) {
      return (
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      );
    }

    return (
      <section className="client-approval">
        {this.renderCampaignInfo()}
        {this.renderPageHeading()}
        {this.renderInfluencerSelectorSection()}
        {
          isEmpty(list) ?
            this.renderEmpty() :
            this.renderApprovalBucket()
        }
      </section>
    );
  }
}

const mapStateToProps = ({ clientApproval, router }): StateProps => ({
  isLoading: clientApproval.isLoading,
  list: clientApproval.list,
  influencers: clientApproval.influencers,
  pagination: clientApproval.pagination,
  campaign: clientApproval.campaign,
  location: router.location
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchClientApproval: (influencerId: number, page: number, sortBy: string) => (
    dispatch(fetchClientApproval(influencerId, page, sortBy))
  ),

  dispatchFetchInfluencers: () => dispatch(fetchInfluencers()),
  dispatchResetClientApproval: () => dispatch(resetClientApproval()),
  dispatchResetInfluencers: () => dispatch(resetInfluencers()),
  dispatchDownloadCampaignContent: (fileName: string) => dispatch(downloadCampaignContent(fileName)),

  dispatchDownloadLook: (bucketId: number, lookName: string, campaignName: string) => (
    dispatch(downloadLook(bucketId, lookName, campaignName))
  ),

  dispatchUpdateBrandNote: (bucketId, note) => dispatch(updateBrandNote(bucketId, note)),
  dispatchUpdateApprovalStatus: (bucketId, itemId, approvalStatus) => dispatch(updateApprovalStatus(bucketId, itemId, approvalStatus))
});

export default connect(mapStateToProps, mapDispatchToProps)(ClientApproval);
